import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { secureZustandStorage } from '@/services/secure-storage.service';
import { useMessagingStore } from './messaging.store';
import { useNotificationStore } from './notification.store';
import { useFormsStore } from './forms.store';
import { useAiAssistantStore } from './ai-assistant.store';
import { useIncidentStore } from './incident.store';
import { queryClient } from '@/config/queryClient';

/**
 * Tell the service worker to evict the URL-keyed `/api` response cache.
 * Guarded for SSR / no-SW environments. Best-effort and non-throwing.
 */
function clearServiceWorkerApiCache(): void {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker.controller?.postMessage({ type: 'CLEAR_API_CACHE' });
  } catch {
    // Non-fatal — the SW also skips caching authenticated responses.
  }
}

/**
 * Tear down every piece of in-memory state that belongs to the tenant we are
 * leaving. Must run on EVERY path that changes tenant identity, not just the
 * explicit TenantSwitcher click.
 *
 * WHY the incident context is cleared here (the part that is easy to miss):
 * `useIncidentStore` keeps the FULL incident object of the tenant we are
 * leaving, and `useActiveIncidentSync` only revalidates when
 * `activeIncidentId && !activeIncident`. So a surviving object permanently
 * suppresses the one check that would notice the incident is unreachable in the
 * new tenant. If this is reverted: the engaged-incident chrome keeps showing
 * the previous tenant's incident, every incident-scoped page (Tasks, Activity
 * Log, Board, ICS forms, Damage Assessments, Supply Requests) re-queries with
 * `incident_id=<tenant A incident>` under `X-Tenant-ID: <tenant B>`, RLS
 * matches nothing and the request returns HTTP 200 with zero rows — during a
 * real disaster the operator reads "no tasks / no open supply requests" as an
 * operational fact. Worse, offline creation paths
 * (`useOfflineForms`/`useOfflinePdaZones`) stamp NEW IndexedDB records with the
 * foreign tenant's incident id, so that field work can never sync.
 */
function clearTenantScopedState(): void {
  useMessagingStore.getState().clearAllState();
  useNotificationStore.getState().clearAllState();
  useFormsStore.getState().clearAllState();
  useAiAssistantStore.getState().clearAllState();
  // Resets to activeIncident=null / activeIncidentId=null / isDailyOps=true —
  // the correct post-switch default (RequireIncident then prompts the operator
  // to engage an incident that actually belongs to the new tenant).
  useIncidentStore.getState().clearIncidentContext();
  queryClient.clear();
  // Evict the service worker's URL-keyed /api cache so the previous
  // tenant's cached GET responses can't survive the switch and leak
  // into the newly-active tenant. (Pending offline mutations are NOT
  // purged — they're tenant-stamped and the replay path skips any that
  // don't match the active tenant, so the previous tenant's queued work
  // is preserved and syncs when that tenant is active again.)
  clearServiceWorkerApiCache();
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  role_id?: string;
  role_name?: string;
  // Default map extent settings
  center_lat?: number;
  center_lng?: number;
  default_map_zoom?: number;
}

interface TenantState {
  tenants: Tenant[];
  activeTenant: Tenant | null;

  setTenants: (tenants: Tenant[]) => void;
  setActiveTenant: (tenant: Tenant | null) => void;
  clearTenants: () => void;
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      tenants: [],
      activeTenant: null,

      setTenants: (tenants) => {
        const state = get();
        // If no active tenant but we have tenants, auto-select first
        if (!state.activeTenant && tenants.length > 0) {
          set({ tenants, activeTenant: tenants[0] });
          return;
        }
        // If active tenant is no longer in list, clear it
        if (state.activeTenant && !tenants.find((t) => t.id === state.activeTenant?.id)) {
          const next = tenants[0] || null;
          // This branch silently changes tenant identity with no user action —
          // it fires live (no reload) when a membership is revoked while the
          // operator is working and TenantSwitcher re-syncs the ['tenants']
          // query into the store. It must tear down tenant-scoped state for
          // exactly the same reasons an explicit switch does.
          if (next?.id !== state.activeTenant.id) {
            clearTenantScopedState();
          }
          set({ tenants, activeTenant: next });
          return;
        }
        set({ tenants });
      },

      setActiveTenant: (tenant) => {
        const prev = get().activeTenant;
        // Clear tenant-scoped stores when switching to a different tenant
        if (prev && tenant && prev.id !== tenant.id) {
          clearTenantScopedState();
        }
        set({ activeTenant: tenant });
      },

      clearTenants: () => set({ tenants: [], activeTenant: null }),
    }),
    {
      name: 'tenant-storage',
      storage: createJSONStorage(() => secureZustandStorage),
      partialize: (state) => ({
        activeTenant: state.activeTenant,
      }),
    }
  )
);
